import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ConsentStatus = 'granted' | 'denied' | null;

@Injectable({
    providedIn: 'root'
})
export class ConsentService {
    private readonly STORAGE_KEY = 'analytics_consent';
    private consentSubject: BehaviorSubject<ConsentStatus>;
    public consent$: Observable<ConsentStatus>;

    constructor() {
        // Initialize with stored consent value or null
        const storedConsent = this.getStoredConsent();
        this.consentSubject = new BehaviorSubject<ConsentStatus>(storedConsent);
        this.consent$ = this.consentSubject.asObservable();
    }

    /**
     * Get current consent status
     */
    getConsent(): ConsentStatus {
        return this.consentSubject.value;
    }

    /**
     * Set user consent and persist to localStorage
     */
    setConsent(granted: boolean): void {
        const status: ConsentStatus = granted ? 'granted' : 'denied';

        try {
            localStorage.setItem(this.STORAGE_KEY, status);
        } catch (error) {
            console.error('Failed to store consent preference:', error);
        }

        this.consentSubject.next(status);
    }

    /**
     * Check if user has made a consent decision
     */
    hasUserDecided(): boolean {
        return this.getConsent() !== null;
    }

    /**
     * Clear consent (for testing or reset purposes)
     */
    clearConsent(): void {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
        } catch (error) {
            console.error('Failed to clear consent preference:', error);
        }

        this.consentSubject.next(null);
    }

    /**
     * Read consent from localStorage
     */
    private getStoredConsent(): ConsentStatus {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (stored === 'granted' || stored === 'denied') {
                return stored;
            }
        } catch (error) {
            console.error('Failed to read consent preference:', error);
        }

        return null;
    }
}
